import { Dexie, type EntityTable } from 'dexie';
import {
  type ActionType,
  type Hashtag,
  type Passage,
  type PrayerListItem,
  type SyncedPrayerItem,
  type UserSetting,
  type Comment,
  type ServerMessage,
  type SyncedInstantiatedPlan,
  type SyncedInstantiatedPlanDay,
  type SyncedPlan,
  type VisualisationProps,
} from '@soulhx/fs-common';

export type ItemSyncStatus = 'synced' | 'pendingCreate' | 'pendingUpdate' | 'pendingDelete';

export interface LocalComment extends Comment {
  syncStatus?: ItemSyncStatus;
}

export interface SyncMeta {
  itemId: string;
  lastSyncedAt: string;
}

export interface LocalUserSetting extends UserSetting {
  lastUpdate: string;
  syncStatus?: ItemSyncStatus;
}

export interface LocalPassage extends Passage {
  syncStatus?: ItemSyncStatus;
}

export interface LocalActionType extends ActionType {
  id: string;
  updateDate: string;
  // see SpecialActionIds in fs-common - undefined for any non-special type
  specialActionId?: string;
  syncStatus?: ItemSyncStatus;
}

// Deliberately not based on `ActionEntry` - its `displayName` field is a
// join done server-side for the regular CRUD endpoints, not something the
// raw entries delta-sync (or a local pending create/update) carries. Resolve
// display names locally against `actionTypes` when rendering instead.
export interface LocalActionEntry {
  id: string;
  actionType: string;
  completed: boolean;
  entryDate: string;
  updateDate: string;
  syncStatus?: ItemSyncStatus;
}

// `&` intersection rather than `interface extends` - VisualisationProps is a
// discriminated union, and intersecting a union with a plain object type
// distributes over each member (preserving the discriminant), which
// `interface extends` can't do.
export type LocalGraph = VisualisationProps & {
  id: string;
  updateDate: string;
  syncStatus?: ItemSyncStatus;
};

// named for the *passage* hashtag catalog specifically - prayers have their
// own separate hashtags server-side (separate DB, separate ids), synced into
// their own `prayerHashtags` table - see LocalPrayerHashtag
export interface LocalPassageHashtag extends Hashtag {
  updateDate?: string;
  deleted?: boolean;
  // informational only - a new tag's placeholder row isn't independently
  // pushed, it's resolved as part of its passage's tag-push step (see
  // PassageSync.ts)
  syncStatus?: ItemSyncStatus;
}

// tags carried as ids (`hashtagIds`), resolved against `prayerHashtags` -
// see PrayerSync.ts. `date` is the server's `create_date`, which (despite
// the name) is overwritten on every edit, and is today's sort key;
// `updateDate` is the sync cursor.
export interface LocalPrayer extends Omit<SyncedPrayerItem, 'deleted'> {
  syncStatus?: ItemSyncStatus;
}

// separate table (not embedded on the parent prayer) so each item carries
// its own syncStatus - see PrayerSync.ts for how pushes are ordered around
// the parent list
export interface LocalPrayerListItem extends PrayerListItem {
  syncStatus?: ItemSyncStatus;
}

// same shape and placeholder semantics as LocalPassageHashtag, for the
// prayer hashtag catalog
export interface LocalPrayerHashtag extends Hashtag {
  updateDate?: string;
  deleted?: boolean;
  syncStatus?: ItemSyncStatus;
}

// server messages ("alerts" in the UI) are admin-written and pull-only - see
// ServerMessageSync.ts - so there's no syncStatus. `text` stays embedded
// (one row per message, all languages) rather than mirroring the server's
// 1:N message/text tables, since the texts are always read together.
export type LocalServerMessage = ServerMessage;

// plans are pull-only: authoring (save/publish/etc.) is online-only, writing
// the server's response straight into dexie - see PlanSync.ts - so there's
// no syncStatus. One row per plan *version* (instance), days embedded, since
// a plan's days only ever change together. Tombstones are deleted on pull,
// so `deleted` isn't kept.
export type LocalPlan = Omit<SyncedPlan, 'deleted'>;

// an instantiated plan only ever changes locally by being unsubscribed
// (`pendingDelete`); subscribing is online-only
export interface LocalInstantiatedPlan extends Omit<SyncedInstantiatedPlan, 'deleted'> {
  syncStatus?: ItemSyncStatus;
}

export type IpDayField = 'completed' | 'scheduledDate';

// days change independently on different devices (mark complete on one,
// catch up on another), so a pending row tracks *which* fields changed and
// only those are pushed - see PlanSync.ts
export interface LocalInstantiatedPlanDay extends SyncedInstantiatedPlanDay {
  syncStatus?: ItemSyncStatus;
  dirtyFields?: IpDayField[];
}

const db = new Dexie('SoulHX') as Dexie & {
  comments: EntityTable<LocalComment, 'id'>;
  syncMeta: EntityTable<SyncMeta, 'itemId'>;
  settings: EntityTable<LocalUserSetting, 'settingId'>;
  passages: EntityTable<LocalPassage, 'id'>;
  passageHashtags: EntityTable<LocalPassageHashtag, 'id'>;
  actionTypes: EntityTable<LocalActionType, 'id'>;
  actionEntries: EntityTable<LocalActionEntry, 'id'>;
  graphs: EntityTable<LocalGraph, 'id'>;
  serverMessages: EntityTable<LocalServerMessage, 'id'>;
  prayers: EntityTable<LocalPrayer, 'id'>;
  prayerListItems: EntityTable<LocalPrayerListItem, 'id'>;
  prayerHashtags: EntityTable<LocalPrayerHashtag, 'id'>;
  plans: EntityTable<LocalPlan, 'planInstanceId'>;
  instantiatedPlans: EntityTable<LocalInstantiatedPlan, 'id'>;
  instantiatedPlanDays: EntityTable<LocalInstantiatedPlanDay, 'id'>;
};

db.version(1).stores({
  comments: 'id, itemId, userId, deleted, createDate',
});

db.version(2).stores({
  syncMeta: 'itemId',
});

db.version(3).stores({
  settings: 'settingId',
});

db.version(4).stores({
  passages: 'id, updateDate, deleted',
  passageHashtags: 'id, updateDate, deleted',
});

db.version(5).stores({
  actionTypes: 'id, updateDate, deleted, specialActionId',
  actionEntries: 'id, actionType, entryDate, updateDate, deleted',
});

db.version(6).stores({
  graphs: 'id, updateDate, deleted',
});

// deliberately no `expiryDate` index: IndexedDB leaves rows out of an index
// when the field is undefined/null, so `where('expiryDate').above(now)`
// would silently drop never-expiring messages. There are only ever a
// handful of rows - read them all and filter in JS instead.
db.version(7).stores({
  serverMessages: 'id',
});

db.version(8).stores({
  prayers: 'id, updateDate, date, deleted',
  prayerListItems: 'id, prayerListId, updateDate, deleted',
  prayerHashtags: 'id, updateDate, deleted',
});

db.version(9).stores({
  plans: 'planInstanceId, planId, updateDate, status',
  instantiatedPlans: 'id, planInstanceId, updateDate',
  instantiatedPlanDays: 'id, instantiatedPlanId, scheduledDate, updateDate',
});

export { db };
